import { Box, Button, Field, Input, Link as CLink, Stack, Text } from '@chakra-ui/react'
import type { MemberMe } from '@ipc/shared'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useId, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { Link as RouterLink, useLocation, useNavigate } from 'react-router'
import { api, isApiError } from '~/api/client'
import { keys, useRegistration } from '~/api/hooks'
import { ActionBar } from '~/components/member/ActionBar'
import { CodeEntry, SIGN_IN_RESEND_MS as RESEND_MS } from '~/components/member/CodeEntry'
import { PasswordField } from '~/components/member/PasswordField'
import { ErrorAlert, useFriendlyError } from '~/components/member/feedback'
import { BackLink } from '~/components/member/FlowStepper'
import { FormStack, INLINE_TARGET, MemberLayout, PageTitle } from '~/components/member/layout'
import { SkyScene, SplitCard } from '~/components/member/SplitCard'
import { StepSkeleton } from '~/components/member/StepSkeleton'
import { WhyLink } from '~/components/member/WhyLink'
import { Rich, useT } from '~/i18n/use-t'
import { useAutoClaim, useSessionClaim } from '~/lib/claim'
import { dropPrivateSession } from '~/lib/session'

const EMAIL_FORM = 'login-email-form'
const PASSWORD_FORM = 'login-form'

/** Signed in: the previous person's cache is dropped and the voter page opens with the voter's card. */
function useSignedIn() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  return (me: MemberMe) => {
    dropPrivateSession(qc)
    qc.setQueryData(keys.me, me)
    navigate('/me')
  }
}

/** The email box of the sign-in page (the same box on the email view and on the password view). */
function EmailField({
  value,
  onChange,
  missing,
  invalid,
  describedBy,
  inputRef,
}: {
  value: string
  onChange: (v: string) => void
  missing: boolean
  invalid?: boolean
  describedBy?: string
  inputRef: React.RefObject<HTMLInputElement | null>
}) {
  const { t } = useT()
  return (
    <Field.Root invalid={invalid || missing}>
      <Field.Label>{t('login.email')}</Field.Label>
      <Input
        type='email'
        autoComplete='username'
        inputMode='email'
        autoCapitalize='none'
        enterKeyHint='next'
        spellCheck={false}
        ref={inputRef}
        value={value}
        aria-describedby={describedBy}
        onChange={(e) => onChange(e.target.value)}
      />
      <Field.ErrorText>{missing ? t('login.errEmail') : null}</Field.ErrorText>
    </Field.Root>
  )
}

type View = 'email' | 'check' | 'password' | 'code'

/**
 * Log in (passwordless spec §6, rulings P1, P11), in the split card of vocdoni-app's sign-in with the sky and the dove
 * beside it. One field, the email, and "Email me a sign-in link"; then "Check your email" with the 6-digit code box
 * (the code works in this browser only, the link on any device). "Use my password instead" signs in with the email
 * and the password, then asks for the emailed code when the account wants one (`{ next: 'code' }`).
 *
 * A finished registration whose cookie is still here is claimed instead (no sign-in after registering).
 */
export default function Login() {
  const { t } = useT()
  const friendly = useFriendlyError()
  const signedIn = useSignedIn()
  const ended = (useLocation().state as { registrationEnded?: boolean } | null)?.registrationEnded === true
  const [view, setView] = useState<View>('email')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [missing, setMissing] = useState<{ email?: boolean; password?: boolean }>({})
  const [resendAt, setResendAt] = useState(0)
  const emailRef = useRef<HTMLInputElement>(null)
  const errorId = useId()

  // A voter back with the registration cookie of a finished registration lands on the voter page.
  const reg = useRegistration()
  const claim = useSessionClaim()
  useAutoClaim(reg.data?.claimable === true, claim)

  const request = useMutation({
    mutationFn: () => api('memberSignInRequest', { body: { email: email.trim() } }),
    onSuccess: () => {
      setResendAt(Date.now() + RESEND_MS)
      setView('check')
    },
  })
  const login = useMutation({
    mutationFn: () => api('memberLogin', { body: { email: email.trim(), password } }),
    onSuccess: (res) => {
      if ('next' in res) {
        setResendAt(Date.now() + RESEND_MS)
        setView('code')
      } else signedIn(res)
    },
  })
  const rejected = login.isError && isApiError(login.error) && login.error.status !== 429 && login.error.status < 500

  const go = (next: View) => {
    request.reset()
    login.reset()
    setMissing({})
    setView(next)
  }
  const changeEmail = (v: string) => {
    setEmail(v)
    setMissing((m) => ({ ...m, email: false }))
  }

  // Claiming (a refusal or a failure leaves the sign-in page as it is).
  if (reg.data?.claimable && !claim.isError)
    return (
      <MemberLayout>
        <StepSkeleton />
      </MemberLayout>
    )

  const submitEmail = (e: FormEvent) => {
    e.preventDefault()
    if (request.isPending) return
    if (!email.trim()) {
      setMissing({ email: true })
      emailRef.current?.focus()
      return
    }
    request.mutate()
  }
  const submitPassword = (e: FormEvent) => {
    e.preventDefault()
    if (login.isPending) return
    const next = { email: !email.trim(), password: !password }
    setMissing(next)
    if (next.email) emailRef.current?.focus()
    if (!next.email && !next.password) login.mutate()
  }

  const links = (
    <Stack gap={3}>
      <CLink
        asChild
        textDecoration='underline'
        minH='12'
        display='inline-flex'
        alignItems='center'
        alignSelf='flex-start'
      >
        <RouterLink to='/forgot'>{t('login.forgot')}</RouterLink>
      </CLink>
      {/* Not inside a checkbox label: the link itself is the 48 px target (AC10). */}
      <Text color='fg.muted'>
        {t('login.notMember')}{' '}
        <CLink asChild textDecoration='underline' css={INLINE_TARGET}>
          <RouterLink to='/join'>{t('login.register')}</RouterLink>
        </CLink>
      </Text>
    </Stack>
  )

  let body: ReactNode
  if (view === 'email')
    body = (
      <>
        <PageTitle title={t('login.title')} />
        {ended && <Text role='status'>{t('login.registrationEnded')}</Text>}
        <FormStack id={EMAIL_FORM} gap={5} onSubmit={submitEmail} noValidate>
          <Text color='fg.muted'>{t('login.intro')}</Text>
          <EmailField value={email} onChange={changeEmail} missing={Boolean(missing.email)} inputRef={emailRef} />
          {request.isError && <ErrorAlert>{friendly(request.error)}</ErrorAlert>}
          <WhyLink label={t('login.usePassword')} color='fg' alignSelf='flex-start' onClick={() => go('password')} />
          {links}
          <ActionBar>
            <Button type='submit' form={EMAIL_FORM} size='lg' w='full' loading={request.isPending}>
              {t('login.sendLink')}
            </Button>
          </ActionBar>
        </FormStack>
      </>
    )
  else if (view === 'check')
    body = (
      <>
        <PageTitle title={t('login.checkTitle')} />
        <Text>
          <Rich k='login.checkText' values={{ email: email.trim() }} />
        </Text>
        <CodeEntry
          formId='login-code-form'
          resendAt={resendAt}
          faq='forgot'
          copy={{
            submit: t('login.signIn'),
            resend: t('login.resendEmail'),
            resent: t('login.resentEmail'),
            changeEmail: t('login.otherEmail'),
            expired: t('login.errCodeExpired'),
            tooMany: t('login.errCodeTooMany'),
            challenge: t('login.errChallenge'),
          }}
          onCheck={async (code) => signedIn(await api('memberSignInCode', { body: { code } }))}
          onResend={async () => {
            await api('memberSignInRequest', { body: { email: email.trim() } })
            setResendAt(Date.now() + RESEND_MS)
          }}
          onChangeEmail={() => go('email')}
        />
        {/* P11: an account that asks for a password and a code gets no link; the answer is the same for everyone. */}
        <Stack gap={1} borderTop='1px solid' borderColor='border' pt={4}>
          <Text color='fg.muted'>{t('login.passwordNote')}</Text>
          <WhyLink label={t('login.usePassword')} color='fg' alignSelf='flex-start' onClick={() => go('password')} />
        </Stack>
      </>
    )
  else if (view === 'password')
    body = (
      <>
        <PageTitle title={t('login.title')} />
        <FormStack id={PASSWORD_FORM} gap={5} onSubmit={submitPassword} noValidate>
          <EmailField
            value={email}
            onChange={changeEmail}
            missing={Boolean(missing.email)}
            invalid={rejected}
            describedBy={rejected ? errorId : undefined}
            inputRef={emailRef}
          />
          <PasswordField
            label={t('login.password')}
            value={password}
            onChange={(v) => {
              setPassword(v)
              setMissing((m) => ({ ...m, password: false }))
            }}
            autoComplete='current-password'
            error={missing.password ? t('login.errPassword') : null}
          />
          {login.isError && <ErrorAlert id={errorId}>{rejected ? t('login.error') : friendly(login.error)}</ErrorAlert>}
          <WhyLink label={t('login.useEmail')} color='fg' alignSelf='flex-start' onClick={() => go('email')} />
          {links}
          <ActionBar>
            <Button type='submit' form={PASSWORD_FORM} size='lg' w='full' loading={login.isPending}>
              {t('login.button')}
            </Button>
          </ActionBar>
        </FormStack>
      </>
    )
  else
    body = (
      <>
        <PageTitle title={t('login.checkTitle')} />
        <Text>{t('login.codeText')}</Text>
        <CodeEntry
          formId='login-password-code-form'
          resendAt={resendAt}
          faq={null}
          copy={{
            submit: t('login.signIn'),
            resend: t('login.codeResend'),
            resent: t('login.codeResent'),
            changeEmail: t('login.backToPassword'),
            expired: t('login.codeExpired'),
            tooMany: t('login.codeTooMany'),
            challenge: t('login.codeChallenge'),
          }}
          onCheck={async (code) => signedIn(await api('memberLoginCode', { body: { code } }))}
          onResend={async () => {
            // A new code is a new password sign-in (the server binds the code to it).
            await api('memberLogin', { body: { email: email.trim(), password } })
            setResendAt(Date.now() + RESEND_MS)
          }}
          onChangeEmail={() => {
            setPassword('')
            go('password')
          }}
        />
      </>
    )

  return (
    <MemberLayout wide>
      <SplitCard showcase={<SkyScene doveWidth={260} />}>
        {/* Back (to the start page: an installed app has no browser Back) sits in the card, where a step's header
            has it; never alone on the canvas beside the card. */}
        <Box alignSelf='flex-start'>
          <BackLink to='/join' />
        </Box>
        {body}
      </SplitCard>
    </MemberLayout>
  )
}
